import styled, { keyframes } from "styled-components";
import {
  SPACING,
  QUERIES,
  SPACING_RESPONSIVE,
  ANIMATION,
} from "../../../../utils/constants";
import { toneColor } from "../../../../utils";
import { HeroTone } from "./types";

/**
 * Animations
 */
export const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

/**
 * AccountsSkeleton Styles
 */
export const SkeletonWrapper = styled.div`
  background: var(--color-background-0);
  animation: ${fadeIn} ${ANIMATION.duration.moderate}
    ${ANIMATION.easing.easeOut};
`;

/* Mirrors HeroStrip: full-width stats band. Taller on mobile where the hero
   stacks (Current Value over Deposited/Withdrawn); a single row from
   tablet-large up. */
export const HeroSkeletonWrapper = styled.div`
  margin: ${SPACING.sm + SPACING.xs}px
    ${SPACING_RESPONSIVE.mobile.chartMargin}px 0;
  border-radius: 12px;
  overflow: hidden;
  height: 240px;

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING.md}px ${SPACING_RESPONSIVE.tablet.chartMargin}px 0;
  }

  @media ${QUERIES.tabletLargeAndUp} {
    height: 160px;
  }

  @media ${QUERIES.laptopAndUp} {
    margin: ${SPACING.md + SPACING.xs}px
      ${SPACING_RESPONSIVE.desktop.chartMargin}px 0;
  }
`;

/* Mirrors FiltersWrapper + FilterGroup: year dropdown + active-only toggle.
   Row with space-between on mobile, left-aligned from tablet up. */
export const FiltersSkeletonWrapper = styled.div`
  margin: ${SPACING_RESPONSIVE.mobile.chartMargin}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.sm + SPACING.xs}px;

  @media ${QUERIES.tabletAndUp} {
    margin: 0 ${SPACING_RESPONSIVE.tablet.chartMargin}px;
    margin-top: ${SPACING_RESPONSIVE.tablet.chartMargin}px;
    justify-content: flex-start;
    gap: ${SPACING.md}px;
  }

  @media ${QUERIES.laptopAndUp} {
    margin: 0 ${SPACING_RESPONSIVE.desktop.chartMargin}px;
    margin-top: ${SPACING_RESPONSIVE.desktop.chartMargin}px;
  }
`;

/* Mirrors ListWrapper: responsive grid of account cards. */
export const AccountsSkeletonWrapper = styled.div`
  display: grid;
  grid-auto-rows: min-content;
  padding: ${SPACING_RESPONSIVE.mobile.cardGridPadding}px;
  grid-template-columns: repeat(auto-fill, minmax(min(380px, 100%), 1fr));
  gap: ${SPACING_RESPONSIVE.mobile.cardGridGap}px;
  margin-bottom: ${SPACING_RESPONSIVE.mobile.sectionGap}px;

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.cardGridPadding}px;
    gap: ${SPACING_RESPONSIVE.tablet.cardGridGap}px;
    margin-bottom: ${SPACING_RESPONSIVE.tablet.sectionGap}px;
  }

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING_RESPONSIVE.desktop.cardGridPadding}px;
    gap: ${SPACING_RESPONSIVE.desktop.cardGridGap}px;
    margin-bottom: ${SPACING_RESPONSIVE.desktop.sectionGap}px;
  }
`;

export const SkeletonBox = styled.div<{ width?: string; height?: string }>`
  width: ${(props) => props.width || "100%"};
  height: ${(props) => props.height || "32px"};
  background: var(--gradient-skeleton);
  background-size: 200% 100%;
  animation: loading 1.5s ${ANIMATION.easing.easeInOut} infinite;
  border-radius: 4px;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

/**
 * AccountsStats Styles
 */
export const HeroStrip = styled.div`
  margin: ${SPACING.sm + SPACING.xs}px
    ${SPACING_RESPONSIVE.mobile.chartMargin}px 0;
  border-radius: 12px;
  border: 1px solid var(--color-card-border);
  background: var(--hero-background);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media ${QUERIES.tabletAndUp} {
    margin: ${SPACING.md}px ${SPACING_RESPONSIVE.tablet.chartMargin}px 0;
  }

  @media ${QUERIES.tabletLargeAndUp} {
    flex-direction: row;
  }

  @media ${QUERIES.laptopAndUp} {
    margin: ${SPACING.md + SPACING.xs}px
      ${SPACING_RESPONSIVE.desktop.chartMargin}px 0;
  }
`;

export const HeroSection = styled.div`
  flex: 1;
  padding: ${SPACING.md + SPACING.xs}px ${SPACING.lg}px;
  display: flex;
  flex-direction: column;
  gap: 0;

  @media ${QUERIES.tabletLargeAndUp} {
    padding: ${SPACING.lg + SPACING.xs}px ${SPACING.xl + SPACING.xs}px;
    padding-right: 0px;
  }
`;

/* Current Value block: vertically centered so it owns the full hero height at 850px+ */
export const HeroPrimarySection = styled(HeroSection)`
  @media ${QUERIES.tabletLargeAndUp} {
    justify-content: center;
  }
`;

/* Right side: stacks the action row, its divider, and the Deposited/Withdrawn pair */
export const HeroRightColumn = styled.div`
  display: flex;
  flex-direction: column;

  @media ${QUERIES.tabletLargeAndUp} {
    flex: 2;
  }
`;

/* Desktop-only action row holding the Create Account button, right-aligned */
export const HeroActions = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${SPACING.md + SPACING.xs}px ${SPACING.lg}px 0;

  @media ${QUERIES.tabletLargeAndUp} {
    padding: ${SPACING.lg + SPACING.xs}px ${SPACING.xl + SPACING.xs}px 0;
  }
`;

/* Horizontal rule sitting over the Deposited/Withdrawn pair, beneath the action row */
export const HeroActionsDivider = styled.div`
  background: var(--color-card-border);
  height: 1px;
  width: 95%;
  flex-shrink: 0;
  margin-top: ${SPACING.md + SPACING.xs}px;
  margin-left: auto;
  margin-right: auto;

  @media ${QUERIES.tabletLargeAndUp} {
    margin-top: ${SPACING.lg + SPACING.xs}px;
  }
`;

export const HeroLabel = styled.p`
  font-size: var(--text-xs);
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
  margin: 0 0 ${SPACING.sm}px;
`;

export const HeroValue = styled.p<{ $tone: HeroTone }>`
  font-size: var(--text-display);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: ${(props) => toneColor(props.$tone)};
  margin: 0 0 ${SPACING.xs + SPACING.xxs}px;
`;

export const HeroSubtitle = styled.p`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
  margin: 0;
`;

/* Horizontal below 850px (between primary section and sub-row), inset vertical at 850px+ */
export const HeroDivider = styled.div`
  background: var(--color-card-border);
  flex-shrink: 0;
  height: 1px;
  width: 95%;
  margin-left: auto;
  margin-right: auto;

  @media ${QUERIES.tabletLargeAndUp} {
    height: auto;
    width: 1px;
    margin: ${SPACING.lg}px 0;
    align-self: stretch;
  }
`;

/* Wraps Deposited + Withdrawn; row on all sizes, fills the right column height */
export const HeroSubRow = styled.div`
  display: flex;
  flex-direction: row;

  @media ${QUERIES.tabletLargeAndUp} {
    flex: 1;
  }

  ${HeroSection} {
    padding: ${SPACING.md}px ${SPACING.md + SPACING.xs}px;

    @media ${QUERIES.tabletLargeAndUp} {
      padding: ${SPACING.lg + SPACING.xs}px ${SPACING.xl + SPACING.xs}px;
    }
  }

  ${HeroValue} {
    font-size: var(--text-xxl);

    @media ${QUERIES.tabletLargeAndUp} {
      font-size: var(--text-display);
    }
  }
`;

/* Always inset vertical — between Deposited and Withdrawn */
export const HeroInnerDivider = styled.div`
  background: var(--color-card-border);
  flex-shrink: 0;
  width: 1px;
  align-self: stretch;
  margin: ${SPACING.md + SPACING.xs}px 0;
`;

/**
 * AccountsFilters Styles
 */
export const FiltersWrapper = styled.div`
  margin: ${SPACING_RESPONSIVE.mobile.chartMargin}px;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm + SPACING.xs}px;
  align-items: stretch;
  margin-bottom: 0px;

  @media ${QUERIES.tabletAndUp} {
    margin: 0 ${SPACING_RESPONSIVE.tablet.chartMargin}px;
    margin-top: ${SPACING_RESPONSIVE.tablet.chartMargin}px;
    flex-direction: row;
    align-items: center;
    gap: ${SPACING.md}px;
  }

  @media ${QUERIES.laptopAndUp} {
    margin: 0 ${SPACING_RESPONSIVE.desktop.chartMargin}px;
    margin-top: ${SPACING_RESPONSIVE.desktop.chartMargin}px;
  }
`;

export const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm + SPACING.xs}px;
  flex: 1;
  justify-content: space-between;

  @media ${QUERIES.tabletAndUp} {
    justify-content: flex-start;
  }
`;

/* Live count of accounts matching the current filters. Right-aligned from
   tablet up; stacks under the controls on mobile. */
export const FilterCount = styled.span`
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-text-secondary);
  white-space: nowrap;

  @media ${QUERIES.tabletAndUp} {
    margin-left: auto;
  }
`;

/**
 * AccountsList Styles
 */
export const ListWrapper = styled.ul`
  list-style: none;
  padding: ${SPACING_RESPONSIVE.mobile.cardGridPadding}px;
  grid-auto-rows: min-content;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(380px, 100%), 1fr));
  gap: ${SPACING_RESPONSIVE.mobile.cardGridGap}px;
  margin: 0;
  margin-bottom: ${SPACING_RESPONSIVE.mobile.sectionGap}px;

  @media ${QUERIES.tabletAndUp} {
    padding: ${SPACING_RESPONSIVE.tablet.cardGridPadding}px;
    gap: ${SPACING_RESPONSIVE.tablet.cardGridGap}px;
    margin-bottom: ${SPACING_RESPONSIVE.tablet.sectionGap}px;
  }

  @media ${QUERIES.laptopAndUp} {
    padding: ${SPACING_RESPONSIVE.desktop.cardGridPadding}px;
    gap: ${SPACING_RESPONSIVE.desktop.cardGridGap}px;
    margin-bottom: ${SPACING_RESPONSIVE.desktop.sectionGap}px;
  }
`;
